import { plural } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import { useQuery } from "@tanstack/react-query";
import { observer } from "mobx-react-lite";
import { useEffect, useState } from "react";

import {
  apiGetPatientsByPatientIdComputedOptions,
  PermissionBit,
  QuickLookTasks,
} from "../../../api";
import { AppModel } from "../../../app/app-model";
import { QuickLookButton } from "../../../components/quick-look/quick-look-button";
import { CellText } from "../../../components/ui-kit/tables/cells/cell-text";
import { DataTable } from "../../../components/ui-kit/tables/data-table";
import {
  ppgConditionsText,
  ppgRelatedFlag,
} from "../../../constants/enum-to-text";
import { hasPermission } from "../../../constants/permissions";
import {
  algorithmOptions,
  algorithmPredicate,
  ppgConditionValueOptions,
  uniqueArrayValueOptions,
  uniqueValueOptions,
} from "../../../utils/filter-presets";
import { ComputedData } from "../common/computed-data";
import { PatientSubpage } from "../common/patient-subpage";
import { PlotCanvas, PlotControls, usePlot } from "../common/plot";
import { AFibPlotModel } from "./a-fib-computed-plot";

export const AFibTable = observer(function AFibTable(props: { app: AppModel }) {
  const { t } = useLingui();
  const patient = props.app.requirePatient();
  const [model] = useState(
    () =>
      new ComputedData(
        [],
        patient.api.permissions,
        patient.api.requireUserUUID(),
        patient.api,
        QuickLookTasks.ppg_afib_ql,
      ),
  );
  const { data, refetch } = useQuery(
    apiGetPatientsByPatientIdComputedOptions({
      path: { patient_id: patient.patientId },
      query: {
        all_versions: true,
        search_string: "AF*probability",
        paginate: false,
      },
    }),
  );

  useEffect(() => {
    if (data) {
      model.data = data.computed_data;
      model.init();
    }
  }, [data, model]);

  const plot = usePlot(
    () => new AFibPlotModel(model, ["AF probability"], "plot-canvas"),
  );

  return (
    <PatientSubpage title={t`AFib`} loading={!model.initialized}>
      <PlotCanvas model={plot} />

      <DataTable
        filterActions={<PlotControls model={plot} />}
        persistState
        editMode="dialog"
        model={model}
        itemKeyField="uuid"
        filterNoun={(count) =>
          t`${plural(count, { one: "result", other: "results" })}`
        }
        columns={[
          {
            header: <Trans>Measured</Trans>,
            attr: "measurement_datetime",
            type: "datetime",
            sort: true,
            initSort: "desc",
          },
          {
            header: <Trans>Type</Trans>,
            attr: "measurement_type",
            type: "string",
            filter: {
              kind: "multi",
              group: t`Result`,
              options: uniqueValueOptions("measurement_type"),
            },
          },
          {
            header: <Trans>Value</Trans>,
            attr: "measurement_value",
            type: "number",
            precision: 2,
          },
          {
            header: <Trans>PPG device</Trans>,
            attr: "ppgDevice",
            type: "string",
            filter: {
              kind: "multi",
              group: t`Result`,
              options: uniqueValueOptions("ppgDevice"),
            },
          },
          {
            header: <Trans>PPG condition</Trans>,
            attr: "ppgConditions",
            type: "string",
            // Same rendering `type: "string"` would give, with the raw
            // conditions swapped for their display text.
            cell: ({ item }) => (
              <CellText
                item={item}
                attr="ppgConditions"
                displayValue={ppgConditionsText(item.ppgConditions)}
              />
            ),
            filter: {
              kind: "multi",
              group: t`Result`,
              options: ppgConditionValueOptions("ppgConditions"),
            },
          },
          {
            header: <Trans>Seerlinq algorithm</Trans>,
            attr: "seerlinq_algorithm",
            filter: {
              kind: "multi",
              group: t`Algorithm`,
              options: algorithmOptions,
              predicate: algorithmPredicate,
            },
            cell: ({ item }) =>
              item.seerlinq_algorithm + " " + item.seerlinq_algorithm_version,
          },
          {
            header: <Trans>Quality flag</Trans>,
            attr: "seerlinq_measurement_quality_flag",
            type: "enum",
            editable: true,
            filter: {
              kind: "multi",
              group: t`Result`,
              options: new Map(Object.entries(ppgRelatedFlag)),
            },
            values: ppgRelatedFlag,
          },
          {
            header: <Trans>Tags</Trans>,
            attr: "tags",
            type: "array",
            filter: {
              kind: "multi",
              group: t`Result`,
              options: uniqueArrayValueOptions("tags"),
            },
          },
          {
            header: <Trans>Comment</Trans>,
            attr: "comment",
            type: "string",
            editable: true,
            multiline: true,
          },
          // The AFib QuickLook endpoint needs AFIB on top of PPG_RAW.
          hasPermission(patient.api.permissions, PermissionBit.AFIB) && {
            header: <Trans>AFib QuickLook</Trans>,
            requiredPermission: PermissionBit.PPG_RAW,
            // The worker takes one recording per report.
            cell: ({ item }) =>
              item.ppgQuickLook && item.ppgs?.length === 1 ? (
                <QuickLookButton
                  label={t`QuickLook`}
                  resultLabel={t`View QuickLook`}
                  model={item.ppgQuickLook}
                  size="small"
                />
              ) : (
                "--"
              ),
          },
        ]}
        onEdit={async (editedField, item) => {
          await patient.updateItem("computed", item.uuid, editedField);
          await refetch();
        }}
      />
    </PatientSubpage>
  );
});
